import axios from 'axios';
import { API } from '../../../backend';
import { useAppContext } from '../../../hooks/context/AppContext';
import { useState, useMemo } from 'react';
import useSilentIdTokenRefresh from "../../../msal/useSilentTokenRefresh";

// Common error handler
const handleError = (error: unknown, context: string) => {
  console.error(`Error ${context}:`, error);
  return null;
};

// Define API Response Type
type APIResponse = {
  currentPage: number;
  totalPages: number;
  totalProjects: number;
  perPage: number;
  projects: Project[];
};

// Interfaces
export interface Project {
  project_key: number;
  Project_Id: number;
  bu: string;
  name: string;
  sclead: string;
  country: string;
  category: string;
  subCategory: string;
  brand: string;
  Launch_Year: string;
  launch_Month: string;
  launch_Week: string;
  ProjectStatus: string;
  bigBet: string;
  Bigbetvalue: string;
  uploadPic: string;
  Risk: string;
  comment: string;
  totalProjects: number;
  issaved: number;
  upload_pic?: string;
  country_key: number;
  category_Key: number;
  subcategory_key: number;
  brand_key: number;
  buid: number;
  sclead_key: number;
  risk_key: number;
  big_bet: number;
  Time_Key: number;
  project_name?: string;
}

interface ProjectFilters {
  country?: string;
  category?: string;
  subCategory?: string;
  brand?: string;
  risk?: string;
  bigBet?: string;
}

interface ProjectCounts {
  projectActive: number;
  projectInactive: number;
  totalProject: number;
}

interface HCGlobalFilters {
  buid: number[];
  countryKey: number[];
  categoryKey: number[];
  subCategoryKey: number[];
  brandKey: number[];
}

interface HCGlobalFiltersResponse {
  data: {
    bu: { key: number; value: string }[];
    country: { key: number; value: string }[];
    category: { key: number; value: string }[];
    subCategory: { key: number; value: string }[];
    brand: { key: number; value: string }[];
  };
}

export interface EngineeringScopePayload {
  Project_Key: string;
  Civils_Scope_Key: string;
  Making_Scope_Key: string;
  Packing_Key: string;
  Eol_Scope_Key: string;
  New_Primary_Pack_Mould: string;
  New_Closure_Mould: string;
  Design_For_Manufacturing_Complete: string;
  Low_Cost_Suppliers_In_Scope: string;
  Comment: string;
  Risk_Key: string;
}

// Custom hook for Engineering Scope API client
export const useEngineeringScopeApiClient = () => {
  const { authData } = useAppContext();
  const [idToken, setIdToken] = useState(null);
  const getIdTokenSilently = useSilentIdTokenRefresh((token: any | null, account: any | null) => {
    setIdToken(token);
  });

  const apiClient = useMemo(() => {
    const instance = axios.create({
      baseURL: API,
      headers: {
        'Content-Type': 'application/json',
        Authorization: idToken ? `Bearer ${idToken}` : `Bearer ${authData.idToken}`,
      },
    });
     // Add response interceptor to handle 401 errors
     instance.interceptors.response.use(
      response => response,
      async (error) => {
        const originalRequest = error.config;

        // If the error is 401 and we haven't already retried
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const {token}  = await getIdTokenSilently();
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            }
          } catch (refreshError) {
            console.error('Error refreshing token:', refreshError);
            // If token refresh fails, redirect to login or handle as needed
            // You might want to add your auth redirect logic here
            window.location.reload();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );

    return instance;
  }, [idToken, authData.idToken]);

  // Lookup APIs
  const getCivilScope = async () => {
    try {
      const response = await apiClient.get('/lookup/ES_Civils_Scope');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching civil scope options');
    }
  };

  const getMakingScope = async () => {
    try {
      const response = await apiClient.get('/lookup/ES_Making_Scope');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching making scope options');
    }
  };

  const getPacking = async () => {
    try {
      const response = await apiClient.get('/lookup/ES_Packing');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching packing options');
    }
  };

  const getEolScope = async () => {
    try {
      const response = await apiClient.get('/lookup/ES_EOL_Scope');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching EOL scope options');
    }
  };

  const getESManufacturingComeple = async () => {
    try {
      const response = await apiClient.get('/lookup/ES_Manufacturing_Comeple');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching manufacturing complete options');
    }
  };

  const getESLowCostSuppliersinScope = async () => {
    try {
      const response = await apiClient.get('/lookup/ES_LowCost_Suppliersin_Scope');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching low cost suppliers options');
    }
  };

  const getTechnicalRisk = async () => {
    try {
      const response = await apiClient.get('/risks');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching technical risks');
    }
  };

  // Engineering Scope CRUD APIs
  const getEngineeringScope = async (project_key: string) => {
    try {
      const response = await apiClient.get(`/engineeringScope/${project_key}`);
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching engineering scope');
    }
  };

  const createEngineeringScope = async (payload: EngineeringScopePayload) => {
    try {
      const response = await apiClient.post('/engineeringScope', payload);
      return response.data;
    } catch (err) {
      return handleError(err, 'creating engineering scope');
    }
  };

  const updateEngineeringScope = async (payload: EngineeringScopePayload, project_key: string) => {
    try {
      const response = await apiClient.put(`/engineeringScope/${project_key}`, payload);
      return response.data;
    } catch (err) {
      return handleError(err, 'updating engineering scope');
    }
  };

  // Static options
  // const getPrimaryPack = () => [
  //   { key: 1, value: "Yes" },
  //   { key: 2, value: "No" },
  // ];

  // const getClosureMould = () => [
  //   { key: 1, value: "Yes" },
  //   { key: 2, value: "No" },
  // ];


  const getPrimaryPack = async () => {
    try {
      const response = await apiClient.get('/lookup/ES_New_Primary_Pack_Mould');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching New Primary Pack Mould options');
    }
  };

  const getClosureMould = async () => {
    try {
      const response = await apiClient.get('/lookup/ES_New_Closure_Mould');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching New Closure Mould options');
    }
  };



  return {
    // Lookup methods
    getCivilScope,
    getMakingScope,
    getPacking,
    getEolScope,
    getESManufacturingComeple,
    getESLowCostSuppliersinScope,
    getTechnicalRisk,

    // Engineering Scope CRUD methods
    getEngineeringScope,
    createEngineeringScope,
    updateEngineeringScope,

    // Static options
    getPrimaryPack,
    getClosureMould,
  };
};